import { Injectable } from '@angular/core';
import { QuestionSchema } from '@core/form-generator-engine/abstractions';
import { Mapper } from '@shared/helpers/mapper';
import { QueryCollection } from '@shared/models/query-collection';
import { EvaluationResponse } from 'src/app/web-api-client';
import { EvaluationQuestion } from '../models/evaluation-question';
import { EvaluationTaxResult } from '../models/evaluation-tax-result';
import { RulesForm } from '../models/rules-form';

@Injectable()
export class RulesFormManager extends QueryCollection<RulesForm> {
  currentIndex = 0;

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(items: Array<RulesForm>) {
    super(items);
  }

  get current(): RulesForm {
    return this[this.currentIndex];
  }

  get data(): EvaluationResponse | undefined {
    return (
      (this[this.currentIndex] && this[this.currentIndex].data) || undefined
    );
  }

  get taxResult(): EvaluationTaxResult[] | undefined {
    return (
      (this[this.currentIndex] &&
        this[this.currentIndex].data &&
        this[this.currentIndex].data!.taxResult) ||
      undefined
    );
  }

  get allQuestions(): EvaluationQuestion[] {
    return this.reduce((result: EvaluationQuestion[], ruleForm: RulesForm) => {
      return result.concat(ruleForm.questions);
    }, []);
  }

  get allTaxResult(): EvaluationTaxResult[] {
    return this.reduce((result: EvaluationTaxResult[], ruleForm: RulesForm) => {
      return result.concat(ruleForm.taxResults);
    }, []);
  }

  get hasQuestions(): boolean {
    return this.some(
      (rules: RulesForm) => rules.questions && rules.questions.length > 0
    );
  }

  next(): number {
    this.currentIndex++;
    return this.currentIndex;
  }

  clear(): void {
    this.splice(0);
    this.currentIndex = 0;
  }

  hasIncompleteForms(): boolean {
    return this.some((ruleForm: RulesForm) => !ruleForm.isCompleted);
  }

  hasTaxResult(): boolean {
    return !!(
      this.at(this.currentIndex) && this.at(this.currentIndex)!.taxResults.any()
    );
  }

  updateQuestion(question: QuestionSchema): void {
    this[this.currentIndex].questions = this[this.currentIndex].questions.map(
      (questionEvaluation: EvaluationQuestion): EvaluationQuestion => {
        if (question.name === questionEvaluation.name) {
          const questionValue = question.value as JSONObject;
          questionEvaluation.answer =
            questionValue && questionValue['value'] !== undefined
              ? questionValue['value'].toString()
              : '';
        }
        return questionEvaluation;
      }
    ) as QueryCollection<EvaluationQuestion>;
  }

  addEvaluation(evaluation: EvaluationResponse): void {
    this[this.currentIndex].data = evaluation;
    this.evaluate(evaluation);
  }

  setLastUnsureValue(evaluation: EvaluationResponse): void {
    this[this.currentIndex].questions.last.answer = evaluation.usedUnsureAnswer;
    this[this.currentIndex].questions.last.unsureAnswer = true;
  }

  override concat(items: Array<RulesForm>): RulesFormManager {
    items.map((item: RulesForm) => {
      this.push(item);
      if (item && item.data) {
        this.evaluate(item.data);
      }
      return item;
    });
    return this;
  }

  private evaluate(evaluation: EvaluationResponse): void {
    if (evaluation) {
      if (evaluation.question) {
        this.addQuestion(evaluation);
      } else if (evaluation.taxResult) {
        this.setTaxResult(evaluation);
      }
    }
  }

  private addQuestion(evaluation: EvaluationResponse): void {
    const questionRequest = Mapper.map(
      evaluation.question,
      EvaluationQuestion,
      [
        'name',
        'questionId',
        {
          taxPolicyId: evaluation.taxPolicyId,
          taxRuleId: evaluation.callTaxRuleId,
        },
      ]
    );
    this[this.currentIndex].questions.push(questionRequest);
  }

  private setTaxResult(evaluation: EvaluationResponse): void {
    const taxResults = Mapper.mapByList(
      evaluation.taxResult ?? [],
      EvaluationTaxResult,
      [
        'description',
        'rate',
        'taxResultItemTypeId',
        'taxResultItemId',
        {
          taxPolicyId: evaluation.taxPolicyId,
          taxRuleId: evaluation.callTaxRuleId,
        },
      ]
    );
    this[this.currentIndex].taxResults = new QueryCollection(
      this[this.currentIndex].taxResults.concat(taxResults)
    );
    this[this.currentIndex].isCompleted = true;
  }
}
